import { type ReactNode, useEffect, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ProductVariationModel } from '@/types/model/ProductVariation'

import { VariantStockAdjustDialog } from './variant-card/VariantStockAdjustDialog'

interface Props {
  variations: ProductVariationModel.ProductVariation[]
  onSuccess: () => void
  /** A stock change the assistant prepared: opens that variation's dialog prefilled. */
  review?: { variationId: string; batchId: string | null; quantity: number; notice: ReactNode; onSaved: () => void; stamp?: string }
}

export const ProductInventorySection = ({ variations, onSuccess, review }: Props) => {
  const [activeVariationId, setActiveVariationId] = useState<string | null>(null)

  // Open the prepared variation's dialog when a review starts (or Review is clicked again).
  const reviewVariationId = review?.variationId
  const reviewStamp = review?.stamp
  useEffect(() => {
    if (reviewVariationId) setActiveVariationId(reviewVariationId)
  }, [reviewVariationId, reviewStamp])

  const activeVariation = variations.find((v) => v._id === activeVariationId)

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Inventory</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Variant</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Batches</TableHead>
                <TableHead className="text-right">Available</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {variations.map((variation) => {
                const stocks = variation.stocks ?? []
                const totalQty = stocks.reduce((sum, s) => sum + s.quantity, 0)
                const isOut = totalQty <= 0
                const isLow = !isOut && totalQty <= 5

                return (
                  <TableRow key={variation._id}>
                    <TableCell className="font-medium text-ink">{variation.name}</TableCell>
                    <TableCell className="font-mono text-xs text-text-2">{variation.sku}</TableCell>
                    <TableCell className="text-right tabular-nums text-text-2">{stocks.length}</TableCell>
                    <TableCell className="text-right tabular-nums text-ink">{totalQty}</TableCell>
                    <TableCell>
                      {isOut ? (
                        <Badge tone="error">Out of stock</Badge>
                      ) : isLow ? (
                        <Badge tone="warning">Low stock</Badge>
                      ) : (
                        <Badge tone="success">In stock</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setActiveVariationId(variation._id)}
                      >
                        Adjust
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
              {variations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                    No variations found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {activeVariation && (
        <VariantStockAdjustDialog
          open={!!activeVariationId}
          onOpenChange={(open) => { if (!open) setActiveVariationId(null) }}
          variationId={activeVariation._id}
          stocks={activeVariation.stocks ?? []}
          onSuccess={() => {
            if (review && review.variationId === activeVariation._id) review.onSaved()
            onSuccess()
          }}
          prefill={review && review.variationId === activeVariation._id ? review : undefined}
          notice={review && review.variationId === activeVariation._id ? review.notice : undefined}
        />
      )}
    </>
  )
}
